import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { PaginatedResponse, SocialPostsImport } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import Form from './component/Form';
import TableRow from './component/TableRow';

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledTable = styled(Table)`
  border-collapse: separate;
  td,
  th {
    vertical-align: top;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
  td {
    &:last-child {
      position: sticky;
      right: 0px;
      top: 0px;
      bottom: 0px;
      z-index: 1;
      border-left: 1px solid rgb(242, 243, 247) !important;
    }
  }
`;

const SocialPostsImporterTool = () => {
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'createdAt',
    direction: SortDirection.DESC,
  });

  const fetchPosts = ({ pageParam = '/v1/tools/social-posts-imports' }) => {
    return api.get<never, PaginatedResponse<SocialPostsImport>>(pageParam, {
      params: {
        ...sortQueryParamObj,
        ...(!!debouncedSearch && {
          'filter[search]': debouncedSearch,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'social_posts_imports',
      {
        sort,
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    fetchPosts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 15 * 1000,
    },
  );

  const imports = useMemo(
    () =>
      data?.pages?.reduce<SocialPostsImport[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  return (
    <>
      <HeadingRow>
        <PageHeading>Social Posts Importer</PageHeading>
        <Button onClick={() => setShowCreationForm(true)}>New search</Button>
      </HeadingRow>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by target list URL"
      />
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Source URL</Th>
              <Th>Created By</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="status"
                title="Status"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="createdAt"
                title="Created At"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="minLikes"
                title="Min. Likes"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="postsSince"
                title="Posts since"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="resultsLimit"
                title="Scrape X Results"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="savablePostsCount"
                title="Top X Results"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="postsImported"
                title="Posts imported"
              />
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!imports?.length}
            >
              {imports?.map((imp) => (
                <TableRow postsImport={imp} key={imp.id} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      <AnimatePresence>
        {showCreationForm && (
          <Form onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default SocialPostsImporterTool;
